import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';
import { useChatPanel } from '../contexts/ChatPanelContext';
import ChatPanel from './ChatPanel';

const MultiModalLayout = ({ children, allowPageScroll = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { 
    shouldShowChatPanel, 
    showChatPanel, 
    hideChatPanel,
    navigateWithChat,
    chatPanelState
  } = useChatPanel();

  // Handle route changes and chat panel visibility
  useEffect(() => {
    const state = location.state;
    
    // Show chat panel if coming from a chat action
    if (state?.showChat) {
      showChatPanel(state.sourceRoute, state.chatParams, { startMinimized: false });
    } 
    // Show chat panel by default on multi-modal pages (unless explicitly hidden)
    else if (!chatPanelState.isVisible && !state?.hideChat) {
      showChatPanel(undefined, null, { startMinimized: isMobile });
    }
    
    // Hide chat panel when navigating to chat page directly
    if (location.pathname === '/chat' && !state?.fromMultiModal) {
      hideChatPanel();
    }
  }, [location, showChatPanel, hideChatPanel, chatPanelState.isVisible, isMobile]);

  // Handle chat actions (navigation, etc.)
  const handleChatAction = (action) => {
    if (!action) return;

    switch (action.type?.toUpperCase()) {
      case 'NAVIGATE':
        // Use navigateWithChat to maintain chat context
        navigateWithChat(action.target, action.params);
        break;
      
      case 'REDIRECT':
        // Direct navigation for redirects
        const url = action.params ? `${action.target}?${action.params}` : action.target;
        window.location.href = url;
        break;
      
      // Add more action types as needed
      default:
        console.warn('Unknown action type:', action.type);
    }
  };

  const showChat = shouldShowChatPanel();
  const chatWidth = showChat ? (isMobile ? 0 : (chatPanelState.isMinimized ? 50 : chatPanelState.width)) : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        height: allowPageScroll ? 'auto' : '100dvh',
        minHeight: '100vh',
        overflowX: 'hidden',
        overflowY: allowPageScroll ? 'visible' : 'hidden',
        backgroundColor: 'transparent',
        backgroundImage: 'none',
      }}
    >
      {/* Chat Panel */}
      {showChat && (
        <ChatPanel onActionExecute={handleChatAction} />
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          width: showChat ? `calc(100% - ${chatWidth}px)` : '100%',
          marginLeft: showChat ? `${chatWidth}px` : 0,
          overflowX: 'hidden',
          overflowY: allowPageScroll ? 'visible' : 'hidden',
          transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out',
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MultiModalLayout;
